/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import { 
  GeofenceGeometry, 
  GeofenceInput, 
  GeofenceType,
  GeofencePolygonGeometry,
  useCreateGeofenceMutationV1,
  useGeofencesQueryV1
} from "@/api/v1/geofence-api";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { GeofenceDraw } from "@/components/map/geofence-draw";
import { GeofenceForm } from "@/components/geofences/geofence-form";
import { toast } from "sonner";
import { Map } from "react-map-gl/maplibre";
import { Button } from "@/components/ui/button";
import { 
  ArrowLeft, 
  MapPin, 
  Settings, 
  Info, 
  CheckCircle,
  AlertTriangle
} from "lucide-react";
import { useStore } from "@/store/store";
import { BreadcrumbNav } from "@/components/ui/breadcrumb-nav";

export function GeofenceCreateRoute() {
  const navigate = useNavigate();
  const [geofenceGeometry, setGeofenceGeometry] = useState<GeofenceGeometry>({
    type: "polygon",
    coordinates: []
  } as GeofencePolygonGeometry);
  const [geofenceType, setGeofenceType] = useState<GeofenceType>("polygon");
  const [step, setStep] = useState<"draw" | "details">("draw");

  const mapStyleUrl = useStore((state) => state.mapStyleUrl);
  const initialViewState = useStore((state) => state.mapBounds);
  const zoom = useStore((state) => state.mapZoom);
  
  const { data: geofences } = useGeofencesQueryV1();
  const createGeofenceMutation = useCreateGeofenceMutationV1();
  
  const MAX_FREE_GEOFENCES = 1;
  const hasReachedLimit = (geofences?.length ?? 0) >= MAX_FREE_GEOFENCES;

  // Redirect if limit is reached
  useEffect(() => {
    if (hasReachedLimit) {
      toast.error(`You've reached the limit of ${MAX_FREE_GEOFENCES} geofence on the Free plan`);
      navigate({ to: "/geofences" as any });
    }
  }, [hasReachedLimit, navigate]);

  const handleGeofenceDrawn = useCallback((geometry: GeofenceGeometry, type: GeofenceType) => {
    setGeofenceGeometry(geometry);
    setGeofenceType(type);
  }, []);

  const handleContinue = () => {
    // Validate that a geofence has been drawn
    const polygonGeometry = geofenceGeometry as GeofencePolygonGeometry;
    if (!polygonGeometry.coordinates || 
        polygonGeometry.coordinates.length === 0 || 
        polygonGeometry.coordinates[0].length < 4) { // At least 3 points + closing point
      toast.error("Please draw a valid polygon with at least 3 points");
      return;
    }
    
    setStep("details");
  };

  const handleSubmit = async (data: GeofenceInput) => {
    try {
      await createGeofenceMutation.mutateAsync(data);
      toast.success("Geofence created successfully");
      navigate({ to: "/geofences" as any });
    } catch (error) {
      toast.error("Failed to create geofence");
      console.error("Create geofence error:", error);
    }
  };

  return (
    <main>
      <div className="container mx-auto p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
        <BreadcrumbNav
          items={[
            { label: "Home", href: "/" },
            { label: "Geofences", href: "/geofences" },
            { label: "Create Geofence" },
          ]}
          className="mb-6"
        />
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              size="icon" 
              onClick={() => navigate({ to: "/geofences" as any })}
              className="h-10 w-10"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold">Create Geofence</h1>
              <p className="text-sm text-muted-foreground">
                Define a custom airspace boundary for drone monitoring
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="w-fit">
              <MapPin className="h-3 w-3 mr-1" />
              Step {step === "draw" ? "1" : "2"} of 2
            </Badge>
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="flex items-center space-x-4">
          <div className={`flex items-center ${step === "draw" ? "text-primary" : "text-muted-foreground"}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === "draw" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
              {step === "draw" ? "1" : <CheckCircle className="h-4 w-4" />}
            </div>
            <span className="ml-2 text-sm font-medium">Draw Boundary</span>
          </div>
          <div className="flex-1 h-px bg-muted"></div>
          <div className={`flex items-center ${step === "details" ? "text-primary" : "text-muted-foreground"}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === "details" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
              2
            </div>
            <span className="ml-2 text-sm font-medium">Configure Details</span>
          </div>
        </div>

        {step === "draw" ? (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Draw Geofence Boundary
              </CardTitle>
              <CardDescription>
                Click on the map to create a polygon boundary for your geofence. 
                You can create either a polygon or circle shape.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="h-[600px] relative">
                <Map
                  id="geofence-map"
                  mapStyle={mapStyleUrl}
                  initialViewState={{
                    longitude: initialViewState[0],
                    latitude: initialViewState[1],
                    zoom: zoom as number
                  }}
                  style={{ width: '100%', height: '100%' }}
                  mapLib={import('maplibre-gl')}
                  attributionControl={false}
                  reuseMaps
                >
                  <GeofenceDraw onGeofenceDrawn={handleGeofenceDrawn} />
                </Map>
              </div>
            </CardContent>
            <div className="p-4 border-t flex justify-between items-center">
              <div className="text-sm text-muted-foreground">
                {geofenceGeometry && (geofenceGeometry as GeofencePolygonGeometry).coordinates && (geofenceGeometry as GeofencePolygonGeometry).coordinates.length > 0 ? (
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span>Boundary drawn successfully</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-orange-600" />
                    <span>Draw a boundary on the map to continue</span>
                  </div>
                )}
              </div>
              <Button 
                onClick={handleContinue}
                disabled={
                  !geofenceGeometry || 
                  !(geofenceGeometry as GeofencePolygonGeometry).coordinates || 
                  (geofenceGeometry as GeofencePolygonGeometry).coordinates.length === 0 || 
                  (geofenceGeometry as GeofencePolygonGeometry).coordinates[0].length < 4
                }
              >
                Continue to Details
              </Button>
            </div>
          </Card>
        ) : (
          <div className="grid gap-4 sm:gap-6 lg:grid-cols-3">
            {/* Geofence Preview */}
            <div className="lg:col-span-1">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Info className="h-5 w-5" />
                    Geofence Preview
                  </CardTitle>
                  <CardDescription>
                    Review your geofence configuration
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm font-medium">Type</p>
                    <Badge variant="secondary" className="mt-1">
                      {geofenceType}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Geometry</p>
                    <p className="text-sm text-muted-foreground">
                      {geofenceGeometry.type}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Status</p>
                    <div className="flex items-center gap-2 mt-1">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm text-green-600">Ready to create</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Configuration Form */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="h-5 w-5" />
                    Geofence Configuration
                  </CardTitle>
                  <CardDescription>
                    Provide details and settings for your {geofenceType} geofence
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <GeofenceForm 
                    onSubmit={handleSubmit} 
                    geofenceGeometry={geofenceGeometry}
                    geometryType={geofenceType}
                    isSubmitting={createGeofenceMutation.isPending}
                  />
                  <div className="mt-6 flex flex-col sm:flex-row gap-2">
                    <Button 
                      variant="outline" 
                      onClick={() => setStep("draw")}
                      className="flex-1 sm:flex-none"
                    >
                      Back to Drawing
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Plan Limit Information */}
        <Alert className="border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950">
          <Info className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-2">
              <p className="font-semibold">Free Plan</p>
              <p>
                You can create up to {MAX_FREE_GEOFENCES} geofence on the Free plan. 
                Upgrade to Pro to create unlimited geofences.
              </p>
            </div>
          </AlertDescription>
        </Alert>

        {/* Help Information */}
        <Alert className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950">
          <Info className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-2">
              <p className="font-semibold">Creating Geofences</p>
              <p>
                {step === "draw" 
                  ? "Draw a boundary on the map by clicking to create points. Complete the shape by connecting back to the starting point. You can create both polygon and circle shapes."
                  : "Configure your geofence settings including name and description. These settings will identify and describe your geofence."
                }
              </p>
            </div>
          </AlertDescription>
        </Alert>
      </div>
    </main>
  );
} 